import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { useT } from "../../shared/i18n";

/** What a view can ask of the shell before it leaves the current route. */
export interface DiscardGuard {
  /** Run a navigation action, asking first when a form has unsaved changes. */
  request: (action: () => void) => void;
  /** Report the mounted form's dirty state; the shell owns the prompt. */
  setDirty: (dirty: boolean) => void;
}

/**
 * Fallback for views rendered outside the shell (tests, isolated stories):
 * navigation is never blocked and no form registers a dirty state.
 */
const NO_GUARD: DiscardGuard = {
  request: (action) => action(),
  setDirty: () => {},
};

const DiscardGuardContext = createContext<DiscardGuard>(NO_GUARD);

/**
 * The shell's guard for a view's unsaved changes (spec §3). Every chrome exit
 * the shell owns — back link, crumb, nav destination, tail sheet/menu and the
 * search launch — runs through {@link DiscardGuard.request}, so an edit form
 * can veto the navigation once instead of each control guarding itself.
 */
export function DiscardGuardProvider({ children }: { children: React.ReactNode }) {
  const t = useT();
  const dirty = useRef(false);
  const [pending, setPending] = useState<(() => void) | null>(null);

  const setDirty = useCallback((next: boolean) => {
    dirty.current = next;
  }, []);

  const request = useCallback((action: () => void) => {
    if (dirty.current) setPending(() => action);
    else action();
  }, []);

  const value = useMemo<DiscardGuard>(() => ({ request, setDirty }), [request, setDirty]);

  const keepEditing = () => setPending(null);
  const discard = () => {
    const action = pending;
    // Clearing first stops the action's own navigation from re-prompting.
    setPending(null);
    dirty.current = false;
    action?.();
  };

  return (
    <DiscardGuardContext.Provider value={value}>
      {children}
      <Dialog
        open={pending !== null}
        onClose={keepEditing}
        fullWidth
        maxWidth="xs"
        data-testid="discard-dialog"
        aria-labelledby="discard-dialog-title"
      >
        <DialogTitle id="discard-dialog-title">{t("discardTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t("discardBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={keepEditing} data-testid="keep-editing">
            {t("keepEditing")}
          </Button>
          <Button color="error" onClick={discard} data-testid="discard-confirm">
            {t("discard")}
          </Button>
        </DialogActions>
      </Dialog>
    </DiscardGuardContext.Provider>
  );
}

/**
 * Access the shell's discard guard. Outside the provider this is a pass-through,
 * so a form stays usable in isolation.
 *
 * @returns the guard's `request` and `setDirty` entry points.
 */
export function useDiscardGuard(): DiscardGuard {
  return useContext(DiscardGuardContext);
}

/**
 * Register a form's unsaved state with the shell for as long as it is mounted.
 * Report `false` from the form after a successful save so the next exit (or the
 * save's own navigation) leaves without asking.
 *
 * @param dirty whether the mounted form currently differs from its baseline.
 */
export function useUnsavedChanges(dirty: boolean): void {
  const guard = useDiscardGuard();

  useEffect(() => {
    guard.setDirty(dirty);
  }, [dirty, guard]);

  useEffect(() => () => guard.setDirty(false), [guard]);
}
